import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import styles from "./Base.module.scss";

import {
  BudgetFormData,
  useClientMonthlyBudgetSchema,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import BudgetForm from "@/features/transactions/components/BudgetForm";
import { ClientBudgetProvider } from "@/lib/contexts/ClientBudget/ClientBudgetProvider";
import { useClientBudget } from "@/lib/hooks/useClientBudget";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePopup } from "@/lib/hooks/usePopup";
import { useSortSearchParams } from "@/lib/hooks/useSortSearchParams";
import useToast from "@/lib/hooks/useToast";
import { BudgetEventType } from "@/lib/utils/clientBudgetEventTypeMap";
import { queryClient } from "@/main";
import { GetClientResponse } from "@/types/response/GetClientResponse";
import { PagedResponse } from "@/types/response/PagedResponse";
import { TransactionType } from "@/types/transaction/TransactionType";

interface EditClientMonthBudgetPopupProps {
  budget: number;
  clientId: string;
  onSuccess: () => void;
}

const EditClientMonthlyBudgetInner = ({
  budget,
  clientId,
  onSuccess,
}: EditClientMonthBudgetPopupProps) => {
  const { disableClose, enableClose, closePopup } = usePopup();
  const { t } = useTranslation();
  const { updateMonthlyBudget } = useClientBudget();
  const { showToast } = useToast();
  const { orderBy, descending } = useSortSearchParams();
  const { page } = usePageSearchParams();
  const { clientMonthlyBudgetSchema } = useClientMonthlyBudgetSchema();

  const methods = useForm<BudgetFormData>({
    resolver: zodResolver(clientMonthlyBudgetSchema),
    mode: "onChange",
    defaultValues: {
      budget: budget || 0,
    },
  });

  const handleSubmit = (data: BudgetFormData) => {
    updateMonthlyBudget.mutate(
      { amount: data.budget },
      {
        onSuccess: (response) => {
          onSuccess();
          showToast(
            t("admin.client.clientBudgetAction.success", { action: "updated" }),
            "success"
          );
          queryClient.setQueryData(
            [
              ...invoiceQueryKeys.cache.transactions.byClient(clientId),
              descending,
              orderBy,
              page,
              10,
            ],
            (oldData?: PagedResponse<TransactionType>) => {
              if (!oldData) return oldData;

              if (budget === response.data.monthlyBudget) return oldData;

              const newTransaction: TransactionType = {
                eventData: {
                  allocableBudget: response.data.allocableBudget,
                  amount: response.data.monthlyBudget,
                  previousAmount: budget,
                },
                clientId2: response.data.clientId2,
                eventId: response.data.id,
                eventType: BudgetEventType.MONTHLY_BUDGET_UPDATED,
                metadata: null,
                timestamp: new Date().toISOString(),
              };
              const newData = [newTransaction, ...(oldData.data || [])].slice(
                0,
                oldData.pageSize
              );
              return {
                ...oldData,
                data: newData,
                totalRecords: oldData.totalRecords + 1,
              };
            }
          );

          queryClient.setQueryData(
            clientQueryKeys.detail(clientId),
            (oldData?: GetClientResponse) => {
              if (!oldData) return oldData;

              return {
                ...oldData,
                allocableBudget: response.data.allocableBudget,
                allocableBudgetWithoutOverspend:
                  response.data.allocableBudgetWithoutOverspend,
                allocatedBudget: response.data.allocatedBudget,
                currentBudget: response.data.currentBudget,
                monthlyBudget: response.data.monthlyBudget,
              };
            }
          );
        },
      }
    );
  };

  useEffect(() => {
    if (updateMonthlyBudget.isPending) {
      disableClose();
    } else {
      enableClose();
    }
  }, [updateMonthlyBudget.isPending, disableClose, enableClose]);

  const errorCode = updateMonthlyBudget.error?.response?.data?.[0]?.errorCode;

  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t("actions.edit", { entity: t("common.monthlyBudget") })}
        </h3>
      </div>
      <BudgetForm
        error={errorCode && t(`errorCodes.${errorCode}`)}
        infoMessage={t(
          "All changes made here apply from the 1st day of the following month."
        )}
        isLoading={updateMonthlyBudget.isPending}
        label={t("common.budget")}
        methods={methods}
        onSubmit={handleSubmit}
      />

      <div className={styles.actions}>
        <Button variant="outline" onClick={closePopup}>
          {t("common.cancel")}
        </Button>
        <Button
          loading={updateMonthlyBudget.isPending}
          onClick={methods.handleSubmit(handleSubmit)}
        >
          <Icons.AddIcon fontSize={24} />
          {t("actions.save", { entity: t("common.budget") })}
        </Button>
      </div>
    </>
  );
};

export const EditClientMonthBudgetPopup = (
  props: EditClientMonthBudgetPopupProps
) => {
  return (
    <ClientBudgetProvider clientId={props.clientId}>
      <EditClientMonthlyBudgetInner {...props} />
    </ClientBudgetProvider>
  );
};
